import { KpiGrid, KpiCard } from '../ui/Page';
import { DueCard } from './DueCard';
import type { DueRow } from './MaintenanceDueTable';

/**
 * How long a run of minutes reads on a tile. `2h 40m`, `40m`, `0m`.
 *
 * ⚠️ Deliberately not `Intl.NumberFormat` with a unit, and deliberately not a Date: this
 * is a DURATION, and every date-shaped tool in the browser would drag the browser's own
 * timezone in behind it. `v2/fleet` is swept for `new Date(` and for `toLocaleDateString`
 * precisely so that cannot happen by accident.
 */
export function hoursAndMinutes(total: number): string {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/**
 * The fleet dashboard (112, approved prototype D2): four numbers, the two things the vans
 * are owed, and the shop's board.
 *
 * Every telemetry element the Commusoft references carry is absent by decision D3 — no
 * mileage chart, no idle alerts, no recent-trips rail. ServWell has no live tracking, so
 * those panels would have no data source, and a dashboard whose tiles are furniture is
 * how a shop learns to stop reading it.
 */
export function FleetOverview({
  vehicleCount,
  plannedMinutes,
  stopsWithoutDuration,
  maintenance,
  reminders,
  leaderboard,
}: {
  vehicleCount: number;
  /** Sum of the day's scheduled visit durations, org-clock resolved on the server. */
  plannedMinutes: number;
  /** How many of the day's calls carry no end time and so counted as zero. */
  stopsWithoutDuration: number;
  maintenance: DueRow[];
  reminders: DueRow[];
  leaderboard: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <KpiGrid>
        <KpiCard label="Vehicles" value={vehicleCount} />
        <KpiCard
          label="Maintenance due"
          value={maintenance.length}
          tone={maintenance.some((m) => m.overdue) ? 'danger' : undefined}
        />
        <KpiCard
          label="Reminders due"
          value={reminders.length}
          tone={reminders.some((r) => r.overdue) ? 'danger' : undefined}
        />
        {/*
         * ⭐ "Scheduled hours today", NOT "Route hours planned today".
         *
         * The approved prototype's fourth tile was route hours FROM THE ROUTING ENGINE
         * (D3) — the sum of each van's driving time. That number costs one billed Google
         * call per van, every time this page renders, and it is `null` on any deployment
         * without a Routes key, which is every deployment today. This tile answers the
         * question the database can actually answer: how many hours of WORK are booked.
         * A different number, so it carries a different name — a tile labelled "route
         * hours" showing booked hours would be wrong rather than merely narrower.
         */}
        <KpiCard label="Scheduled hours today" value={hoursAndMinutes(plannedMinutes)} />
      </KpiGrid>

      {/*
       * The caveat belongs beside the number, not in a tooltip. Both `scheduled_end_at`
       * and `scheduled_start_at` are nullable, so a call booked without an end contributes
       * zero — silently, and always downward. A shop reading "3h 20m" over a day with four
       * open-ended calls would plan against a figure that cannot be right.
       */}
      {stopsWithoutDuration > 0 ? (
        <p className="text-xs text-muted-foreground">
          {stopsWithoutDuration === 1
            ? '1 call has no end time, so it adds nothing to the hours above.'
            : `${stopsWithoutDuration} calls have no end time, so they add nothing to the hours above.`}
        </p>
      ) : null}

      {/*
       * `minmax(0,1fr)` rather than a bare `lg:grid-cols-2`: a grid track sized `1fr`
       * still gives its item `min-width: auto`, so a card with one long unbreakable line
       * widens the track and the page scrolls sideways. Pinned on the TRACK as well as on
       * the card so the next card dropped in here inherits the fix instead of rediscovering
       * the bug.
       */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <DueCard
          title="Maintenance due"
          rows={maintenance}
          total={maintenance.length}
          viewAllTab="maintenance"
          emptyText="Nothing owed on the vans. Schedule a service from an asset's page and it appears here until it's done."
        />
        <DueCard
          title="Reminders due"
          rows={reminders}
          total={reminders.length}
          viewAllTab="reminders"
          emptyText="No paperwork coming up. Insurance, registration and inspection dates live on the asset's page."
        />
      </div>

      {leaderboard}
    </div>
  );
}
